import { useCallback, useEffect, useMemo, useRef } from "react";

const DEFAULT_TIMEOUT_MS = 90_000;

/**
 * Detects a dispatcher->async-worker pair (ws/chat_worker, questionnaires
 * qfiles_parse_worker, library process_document) that died at the platform
 * level (Lambda timeout/OOM/throttled Event invoke; retry_attempts=0, no
 * DLQ) after the dispatcher's fast frames (start/status) already arrived.
 * A live worker streams frames continuously for up to five minutes, so
 * silence for the whole window means it is gone — no error frame will ever
 * arrive on its own, and without this the caller sees an indefinite spinner.
 *
 * - arm(): start the timer once the worker has taken over the turn (e.g.
 *   right after the dispatcher's "start" frame).
 * - reset(): restart the timer — call on every subsequent frame that proves
 *   the worker is still alive (status/tool_start/tool_end/delta/title/...).
 *   Identical to arm(); kept as a separate name for call-site clarity.
 * - clear(): stop watching — call on terminal frames (done/error), on
 *   socket close, on explicit user cancel, and on unmount.
 */
export function useTurnWatchdog(onSilence: () => void, timeoutMs: number = DEFAULT_TIMEOUT_MS) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref mirror so a stale onSilence closure never fires — the caller can
  // pass a fresh callback on every render without re-arming the timer.
  const onSilenceRef = useRef(onSilence);
  onSilenceRef.current = onSilence;

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const arm = useCallback(() => {
    clear();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onSilenceRef.current();
    }, timeoutMs);
  }, [clear, timeoutMs]);

  const reset = arm;

  // Belt-and-suspenders: clear on unmount so a pending timer never fires
  // (and touches state) after the component is gone.
  useEffect(() => clear, [clear]);

  // Stable identity (arm/reset/clear are themselves stable) so callers can
  // safely put the returned object in a useCallback/useEffect deps array
  // without causing needless re-creation on every render.
  return useMemo(() => ({ arm, reset, clear }), [arm, reset, clear]);
}
